import type { Lead, LeadStatus } from "../types";

export const LEAD_STATUSES: LeadStatus[] = [
  "neu",
  "uebernommen",
  "erreicht",
  "termin",
  "abschluss",
  "verloren",
];

export const LEAD_SOURCES = [
  "meta",
  "google",
  "landingpage",
  "whatsapp",
  "manuell",
];

/** Ab hier gilt der Erstkontakt als zu langsam (Versprechen: unter 60 Sekunden). */
export const RESPONSE_TARGET_SECONDS = 60;

/** Leads, bei denen jemand am Telefon war oder geantwortet hat. */
const REACHED_STATUSES: LeadStatus[] = ["erreicht", "termin", "abschluss"];
const APPOINTMENT_STATUSES: LeadStatus[] = ["termin", "abschluss"];

export const formatDuration = (seconds?: number | null) => {
  if (seconds == null) return "–";
  if (seconds < 60) return `${Math.round(seconds)} Sek.`;
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} Min.`;
  if (totalMinutes < 1440) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return minutes ? `${hours} Std. ${minutes} Min.` : `${hours} Std.`;
  }
  const days = Math.round(seconds / 86400);
  return days === 1 ? "1 Tag" : `${days} Tage`;
};

export const median = (values: number[]) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : Math.round((sorted[middle - 1] + sorted[middle]) / 2);
};

export interface LeadKpis {
  total: number;
  open: number;
  responseMedian: number | null;
  responseWithinTarget: number;
  claimMedian: number | null;
  reached: number;
  appointments: number;
  closed: number;
  reachedRate: number;
  appointmentRate: number;
  closeRate: number;
}

export const computeKpis = (leads: Lead[]): LeadKpis => {
  const total = leads.length;
  const responseTimes = leads
    .map((lead) => lead.first_response_seconds)
    .filter((value): value is number => value != null);
  const claimTimes = leads
    .map((lead) => lead.claim_seconds)
    .filter((value): value is number => value != null);
  const reached = leads.filter((lead) =>
    REACHED_STATUSES.includes(lead.status),
  ).length;
  const appointments = leads.filter((lead) =>
    APPOINTMENT_STATUSES.includes(lead.status),
  ).length;
  const closed = leads.filter((lead) => lead.status === "abschluss").length;

  const rate = (part: number) => (total ? Math.round((part / total) * 100) : 0);

  return {
    total,
    open: leads.filter((lead) => lead.sales_id == null).length,
    responseMedian: median(responseTimes),
    responseWithinTarget: responseTimes.length
      ? Math.round(
          (responseTimes.filter((value) => value <= RESPONSE_TARGET_SECONDS)
            .length /
            responseTimes.length) *
            100,
        )
      : 0,
    claimMedian: median(claimTimes),
    reached,
    appointments,
    closed,
    reachedRate: rate(reached),
    appointmentRate: rate(appointments),
    closeRate: rate(closed),
  };
};

/** Kennzahlen gruppiert, z. B. nach Vertriebler, Quelle oder Kampagne. */
export const groupKpis = (
  leads: Lead[],
  keyOf: (lead: Lead) => string | null | undefined,
  fallback: string,
) => {
  const groups = new Map<string, Lead[]>();
  for (const lead of leads) {
    const key = keyOf(lead) || fallback;
    const group = groups.get(key);
    if (group) group.push(lead);
    else groups.set(key, [lead]);
  }
  return [...groups.entries()]
    .map(([key, groupLeads]) => ({ key, ...computeKpis(groupLeads) }))
    .sort((a, b) => b.total - a.total);
};
